import { makeAutoObservable } from 'mobx';
import { AdHocData } from '../tactics/setups/adHoc/addHocData';
import { dataStoreMap } from './dataStoreMap';
import { OptionManagement } from './tradePlanClasses/optionManagement';
import { RestartSettingsData } from './tradePlanClasses/restartSettings';
import { validateTradePlan } from './validateTradePlan';

// NOTE this doesn't seem to use User Preferences to set anything -- definitely not evaluateQuotes
export class TradePlanData {
  root = null;
  status = 'ARMED';
  brokerage = 'BACKTEST';
  dataSource = 'LIVE';
  accountNumber = 'BACKTEST';
  symbol = 'SPY';
  goLong = true;
  goShort = false;
  instrument = 'stock';
  startPeriod = 'open';
  strategy = 'BASIC_TRADING_SYSTEM';
  quantityBasis = 'DOLLARS';

  workOrders = false;
  workOrdersOnEntry = false;
  workOrdersOnExit = false;
  getAwayOffsetPct = 10;
  pctBetweenBidAsk = 50;
  pctIncreasePerOrderAttempt = 10;
  useDifferentWorkOrderFieldsOnExit = false;
  getAwayOffsetPctOnExit = 10;
  pctBetweenBidAskOnExit = 50;
  pctIncreasePerOrderAttemptOnExit = 10;
  useDifferentWorkOrderFieldsOnTarget = false;
  getAwayOffsetPctOnTarget = 10;
  pctBetweenBidAskOnTarget = 50;
  pctIncreasePerOrderAttemptOnTarget = 10;

  restartSystemOncePositionHasFlattened = false;
  restartSettings = new RestartSettingsData();

  flattenAtMarketClose = false;
  terminateWhenFlat = true;
  persistTradePlanOnFailedFiltersOrValidation = false;

  setup = 'AD_HOC';
  setupDetails = {
    AD_HOC: new AdHocData(),
  };
  setupFilters = {};
  entryTactics = {
    MARKET_ORDER: new dataStoreMap.MARKET_ORDER({
      quantityBasisValue: 0,
    }),
  };
  positionTactics = {};
  exitTactics = {};

  optionManagement = {};
  startHour = 9;
  startMin = 30;
  savedConfigurationTitle = '';
  useSavedConfigTitleForDisplay = false;
  evaluateQuotes = false;

  validationStatus = {
    status: true,
    message: '',
  };

  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
  }

  setFormToTemplate = (json) => {
    const regex = /(pct)/i;
    Object.keys(json).forEach((field) => {
      if (field === 'setupDetails') {
        if (json.setup) {
          this.setupDetails = {
            [json.setup]: new dataStoreMap[json.setup](
              json.setupDetails[json.setup]
            ),
          };
        } else {
          this.setupDetails = {};
        }
      } else if (field === 'setupFilters') {
        const filterArray = Object.keys(json.setupFilters);
        if (filterArray && filterArray.length > 0) {
          filterArray.forEach(
            (filter) =>
              (this.setupFilters[filter] = new dataStoreMap[filter](
                json.setupFilters[filter]
              ))
          );
        } else {
          this.setupFilters = {};
        }
      } else if (field === 'restartSettings') {
        this.restartSettings = new RestartSettingsData(json.restartSettings);
      } else if (field === 'optionManagement') {
        if (json.optionManagement) {
          if (Object.keys(json.optionManagement).length === 0) {
            this.optionManagement = {};
          } else {
            this.optionManagement = new OptionManagement(
              this.root,
              this,
              json.optionManagement
            );
          }
        }
      } else if (field === 'entryTactics') {
        const entryTactics = {};
        const tacticArr = Object.entries(json.entryTactics);
        if (tacticArr.length > 0) {
          tacticArr.forEach(([tacticName, tacticObj]) => {
            entryTactics[tacticName] = new dataStoreMap[tacticName](tacticObj);
          });
          this.entryTactics = entryTactics;
        }
      } else if (field === 'positionTactics') {
        const positionTactics = {};
        const tacticArr = Object.entries(json.positionTactics);
        tacticArr.forEach(([tacticName, tacticObj]) => {
          positionTactics[tacticName] = new dataStoreMap[tacticName](tacticObj);
        });
        this.positionTactics = positionTactics;
      } else if (field === 'exitTactics') {
        const exitTactics = {};
        const tacticArr = Object.entries(json.exitTactics);
        tacticArr.forEach(([tacticName, tacticObj]) => {
          exitTactics[tacticName] = new dataStoreMap[tacticName](tacticObj);
        });
        this.exitTactics = exitTactics;
      } else if (regex.test(field)) {
        const translatedValue = Number(json[field]) * 100;
        this[field] = translatedValue;
      } else {
        this[field] = json[field];
      }
    });
  };

  //setters
  setBrokerage = (string) => {
    this.brokerage = string;
  };
  setDataSource = (string) => {
    this.dataSource = string;
    if (this.instrument === 'option') {
      this.optionManagement.setDataSource(string);
    }
  };
  setAccountNumber = (string) => {
    this.accountNumber = string;
  };
  setGoLong = (bool) => {
    this.goLong = bool;
  };
  setGoShort = (bool) => {
    this.goShort = bool;
  };
  get direction() {
    if (this.goLong && this.goShort) return 'BOTH';
    else if (this.goLong && !this.goShort) return 'LONG';
    else if (!this.goLong && this.goShort) return 'SHORT';
    else return 'NONE';
  }
  setDirection = (string) => {
    switch (string) {
      case 'LONG':
        this.goLong = true;
        this.goShort = false;
        break;
      case 'SHORT':
        this.goLong = false;
        this.goShort = true;
        break;
      case 'BOTH':
        this.goLong = true;
        this.goShort = true;
        break;
      default:
        break;
    }
  };
  setInstrument = (string) => {
    if (string === 'option') {
      this.optionManagement = new OptionManagement(this.root, this, {
        dataSource: this.dataSource,
      });
    }
    this.instrument = string;
  };
  setStartPeriod = (string) => {
    this.startPeriod = string;
  };
  setStrategy = (string) => {
    this.strategy = string;
  };
  setSetup = (string, initialData = {}) => {
    this.setup = string;
    if (string !== 'default') {
      this.setupDetails = {
        [string]: new dataStoreMap[string](initialData),
      };
    }
  };
  setQuantityBasis = (string) => {
    this.quantityBasis = string;
  };

  //work orders
  setWorkOrders = (bool) => {
    this.workOrders = bool;
  };
  setWorkOrdersOnEntry = (bool) => {
    this.workOrdersOnEntry = bool;
  };
  setGetAwayOffsetPct = (number) => {
    this.getAwayOffsetPct = Number(number);
  };
  setPctBetweenBidAsk = (number) => {
    this.pctBetweenBidAsk = Number(number);
  };
  setPctIncreasePerOrderAttempt = (number) => {
    this.pctIncreasePerOrderAttempt = Number(number);
  };
  //on exit
  setWorkOrdersOnExit = (bool) => {
    this.workOrdersOnExit = bool;
  };
  setUseDifferentWorkOrderFieldsOnExit = (bool) => {
    this.useDifferentWorkOrderFieldsOnExit = bool;
  };
  setGetAwayOffsetPctOnExit = (number) => {
    this.getAwayOffsetPctOnExit = Number(number);
  };
  setPctBetweenBidAskOnExit = (number) => {
    this.pctBetweenBidAskOnExit = Number(number);
  };
  setPctIncreasePerOrderAttemptOnExit = (number) => {
    this.pctIncreasePerOrderAttemptOnExit = Number(number);
  };

  //on target
  setUseDifferentWorkOrderFieldsOnTarget = (bool) => {
    this.useDifferentWorkOrderFieldsOnTarget = bool;
  };
  setGetAwayOffsetPctOnTarget = (number) => {
    this.getAwayOffsetPctOnTarget = Number(number);
  };
  setPctBetweenBidAskOnTarget = (number) => {
    this.pctBetweenBidAskOnTarget = Number(number);
  };
  setPctIncreasePerOrderAttemptOnTarget = (number) => {
    this.pctIncreasePerOrderAttemptOnTarget = Number(number);
  };

  ///
  setRestartSystemOncePositionHasFlattened = (bool) => {
    this.restartSystemOncePositionHasFlattened = bool;
  };
  setFlattenAtMarketClose = (bool) => {
    this.flattenAtMarketClose = bool;
  };
  setTerminateWhenFlat = (bool) => {
    this.terminateWhenFlat = bool;
  };
  setPersistTradePlanOnFailedFiltersOrValidation = (bool) => {
    this.persistTradePlanOnFailedFiltersOrValidation = bool;
  };
  setSymbol = (string) => {
    this.symbol = string;
  };
  addSetupFilter = (filter) => {
    this.setupFilters[filter] = new dataStoreMap[filter]();
  };
  removeSetupFilter = (filter) => {
    delete this.setupFilters[filter];
  };
  clearSetupFilters = () => {
    this.setupFilters = {};
  };
  get currentEntryTactic() {
    return Object.keys(this.entryTactics)[0];
  }

  get currentQuantityBasisValue() {
    return this.entryTactics[this.currentEntryTactic].quantityBasisValue;
  }

  setQuantityBasisValue = (number) => {
    const entryTactic = this.currentEntryTactic;
    this.entryTactics[entryTactic].setQuantityBasisValue(number);
  };
  setEntryTactic = (string) => {
    const quantityBasis = 0;
    const Class = dataStoreMap[string];
    this.entryTactics = {
      [string]: new Class({
        quantityBasisValue: quantityBasis,
      }),
    };
  };
  addEntryTactic = (string, initialData = {}) => {
    this.entryTactics[string] = new dataStoreMap[string](initialData);
  };
  removeEntryTactic = (string) => {
    const quantityBasis = this.currentQuantityBasisValue;
    if (this.entryTactics[string]) {
      delete this.entryTactics[string];
      if (Object.keys(this.entryTactics).length === 0)
        this.addEntryTactic('MARKET_ORDER', {
          quantityBasisValue: quantityBasis,
        });
    }
  };
  addPositionTactic = (string, initialData = {}) => {
    this.positionTactics[string] = new dataStoreMap[string](initialData);
  };
  removePositionTactic = (string) => {
    delete this.positionTactics[string];
  };
  addExitTactic = (string, initialData = {}) => {
    this.exitTactics[string] = new dataStoreMap[string](initialData);
  };
  removeExitTactic = (string) => {
    delete this.exitTactics[string];
  };
  setStartHour = (number) => {
    this.startHour = number;
  };
  setStartMin = (number) => {
    this.startMin = number;
  };
  setSavedConfigurationTitle = (string) => {
    this.savedConfigurationTitle = string;
  };
  setUseSavedConfigTitleForDisplay = (bool) => {
    this.useSavedConfigTitleForDisplay = bool;
  };
  setEvaluateQuotes = (bool) => {
    this.evaluateQuotes = bool;
  };
  buildSetupFilterObject = () => {
    return Object.entries(this.setupFilters).reduce((acc, item) => {
      acc[item[0]] = item[1].returnJson();
      return acc;
    }, {});
  };
  buildEntryTacticsObject = () => {
    return Object.entries(this.entryTactics).reduce((acc, item) => {
      acc[item[0]] = item[1].returnJson();
      return acc;
    }, {});
  };
  buildPositionTacticsObject = () => {
    return Object.entries(this.positionTactics).reduce((acc, item) => {
      acc[item[0]] = item[1].returnJson();
      return acc;
    }, {});
  };
  buildExitTacticsObject = () => {
    return Object.entries(this.exitTactics).reduce((acc, item) => {
      acc[item[0]] = item[1].returnJson();
      return acc;
    }, {});
  };

  bypassSetup = () => {
    this.setup = 'AD_HOC';
    this.setupDetails = {
      AD_HOC: {},
    };
  };
  clearEntryTactics = () => {
    this.entryTactics = {};
  };

  resetAfterTemplate = () => {
    this.goLong = true;
    this.goShort = false;
    this.instrument = 'stock';
    this.startPeriod = 'open';
    this.strategy = 'BASIC_TRADING_SYSTEM';

    this.workOrders = false;
    this.workOrdersOnEntry = false;
    this.workOrdersOnExit = false;
    this.getAwayOffsetPct = 10;
    this.pctBetweenBidAsk = 50;
    this.pctIncreasePerOrderAttempt = 10;
    this.useDifferentWorkOrderFieldsOnExit = false;
    this.getAwayOffsetPctOnExit = 10;
    this.pctBetweenBidAskOnExit = 50;
    this.pctIncreasePerOrderAttemptOnExit = 10;
    this.useDifferentWorkOrderFieldsOnTarget = false;
    this.getAwayOffsetPctOnTarget = 10;
    this.pctBetweenBidAskOnTarget = 50;
    this.pctIncreasePerOrderAttemptOnTarget = 10;

    this.restartSystemOncePositionHasFlattened = false;
    this.restartSettings = new RestartSettingsData();

    this.flattenAtMarketClose = false;
    this.terminateWhenFlat = true;
    this.persistTradePlanOnFailedFiltersOrValidation = false;

    this.setup = 'AD_HOC';
    this.setupDetails = {
      AD_HOC: new AdHocData(),
    };
    this.setupFilters = {};
    this.entryTactics = {
      MARKET_ORDER: {
        quantityBasisValue: 0,
      },
    };
    this.positionTactics = {};
    this.exitTactics = {};

    this.optionManagement = {};
    this.startHour = 9;
    this.startMin = 30;
    this.savedConfigurationTitle = '';
    this.useSavedConfigTitleForDisplay = false;
    this.evaluateQuotes = false;
  };
  resetToDefaults = () => {
    this.status = 'ARMED';
    this.brokerage = 'BACKTEST';
    this.dataSource = 'LIVE';
    this.accountNumber = 'BACKTEST';
    this.symbol = '';
    this.goLong = true;
    this.goShort = false;
    this.instrument = 'stock';
    this.startPeriod = 'open';
    this.strategy = 'BASIC_TRADING_SYSTEM';
    this.quantityBasis = 'DOLLARS';

    this.workOrders = false;
    this.workOrdersOnEntry = false;
    this.workOrdersOnExit = false;
    this.getAwayOffsetPct = 10;
    this.pctBetweenBidAsk = 50;
    this.pctIncreasePerOrderAttempt = 10;
    this.useDifferentWorkOrderFieldsOnExit = false;
    this.getAwayOffsetPctOnExit = 10;
    this.pctBetweenBidAskOnExit = 50;
    this.pctIncreasePerOrderAttemptOnExit = 10;
    this.useDifferentWorkOrderFieldsOnTarget = false;
    this.getAwayOffsetPctOnTarget = 10;
    this.pctBetweenBidAskOnTarget = 50;
    this.pctIncreasePerOrderAttemptOnTarget = 10;

    this.restartSystemOncePositionHasFlattened = false;
    this.restartSettings = new RestartSettingsData();

    this.flattenAtMarketClose = false;
    this.terminateWhenFlat = true;
    this.persistTradePlanOnFailedFiltersOrValidation = false;

    this.setup = 'AD_HOC';
    this.setupDetails = {
      AD_HOC: new AdHocData(),
    };
    this.setupFilters = {};
    this.entryTactics = {
      MARKET_ORDER: new dataStoreMap.MARKET_ORDER({
        quantityBasisValue: 0,
      }),
    };
    this.positionTactics = {};
    this.exitTactics = {};

    this.optionManagement = {};
    this.startHour = 9;
    this.startMin = 30;
    this.savedConfigurationTitle = '';
    this.useSavedConfigTitleForDisplay = false;
    this.evaluateQuotes = false;
  };
  validate = () => {
    return validateTradePlan(this);
  };
  setValidationStatus = (statusObj) => {
    this.validationStatus = statusObj;
    setTimeout(this.clearValidationStatus, 10000);
  };

  clearValidationStatus = () => {
    this.validationStatus = {
      status: true,
      message: '',
    };
  };

  returnJson = () => {
    return {
      status: this.status,
      brokerage: this.brokerage,
      dataSource: this.dataSource,
      accountNumber: this.accountNumber,
      goLong: this.goLong,
      goShort: this.goShort,
      instrument: this.instrument,
      startPeriod: this.startPeriod,
      strategy: this.strategy,
      setup: this.setup,
      setupDetails: {
        [this.setup]: this.setupDetails[this.setup].returnJson(),
      },
      entryTactics: this.buildEntryTacticsObject(),
      positionTactics: this.buildPositionTacticsObject(),
      exitTactics: this.buildExitTacticsObject(),
      quantityBasis: this.quantityBasis,
      workOrders: this.workOrders,
      workOrdersOnEntry: this.workOrdersOnEntry,
      workOrdersOnExit: this.workOrdersOnExit,
      getAwayOffsetPct: Number(this.getAwayOffsetPct) / 100,
      pctBetweenBidAsk: Number(this.pctBetweenBidAsk) / 100,
      pctIncreasePerOrderAttempt: Number(this.pctIncreasePerOrderAttempt) / 100,
      useDifferentWorkOrderFieldsOnExit: this.useDifferentWorkOrderFieldsOnExit,
      getAwayOffsetPctOnExit: Number(this.getAwayOffsetPctOnExit) / 100,
      pctBetweenBidAskOnExit: Number(this.pctBetweenBidAskOnExit) / 100,
      pctIncreasePerOrderAttemptOnExit:
        Number(this.pctIncreasePerOrderAttemptOnExit) / 100,
      useDifferentWorkOrderFieldsOnTarget:
        this.useDifferentWorkOrderFieldsOnTarget,
      getAwayOffsetPctOnTarget: Number(this.getAwayOffsetPctOnTarget) / 100,
      pctBetweenBidAskOnTarget: Number(this.pctBetweenBidAskOnTarget) / 100,
      pctIncreasePerOrderAttemptOnTarget:
        Number(this.pctIncreasePerOrderAttemptOnTarget) / 100,
      restartSystemOncePositionHasFlattened:
        this.restartSystemOncePositionHasFlattened,
      restartSettings: this.restartSettings.returnJson(),
      flattenAtMarketClose: this.flattenAtMarketClose,
      terminateWhenFlat: this.terminateWhenFlat,
      persistTradePlanOnFailedFiltersOrValidation:
        this.persistTradePlanOnFailedFiltersOrValidation,
      symbol: this.symbol,
      setupFilters: this.buildSetupFilterObject(),
      optionManagement:
        this.instrument === 'option' ? this.optionManagement.returnJson() : {},
      startHour: this.startHour,
      startMin: this.startMin,
      savedConfigurationTitle: this.savedConfigurationTitle,
      useSavedConfigTitleForDisplay: this.useSavedConfigTitleForDisplay,
      evaluateQuotes: this.evaluateQuotes,
    };
  };
}

/* 
{
    "getAwayOffsetPct": 0.1,
    "pctBetweenBidAsk": 50,
    "pctIncreasePerOrderAttempt": 0.1,
    "quantityBasis": "DOLLARS",
    "status": "ARMED",
    "dataSource": "SIMULATED",
    "brokerage": "BACKTEST",
    "accountNumber": "BACKTEST",
    "goLong": true,
    "goShort": false,
    "workOrders": true,
    "workOrdersOnEntry": true,
    "workOrdersOnExit": true,
    "strategy": "BASIC_TRADING_SYSTEM",
    "instrument": "option",
    "symbol": "aapl",
    "startPeriod": "open",
    "setup": "BREAK",
    "terminateWhenFlat": true,
    "restartSystemOncePositionHasFlattened": true,
    "flattenAtMarketClose": false,
    "startHour": 9,
    "startMin": 30,
    "evaluateQuotes": true,
    "persistTradePlanOnFailedFiltersOrValidation": false,
    "useDifferentWorkOrderFieldsOnExit": false,
    "getAwayOffsetPctOnExit": 0.1,
    "pctBetweenBidAskOnExit": 0.5,
    "pctIncreasePerOrderAttemptOnExit": 0.1,
    "useDifferentWorkOrderFieldsOnTarget": false,
    "getAwayOffsetPctOnTarget": 0.1,
    "pctBetweenBidAskOnTarget": 0.5,
    "pctIncreasePerOrderAttemptOnTarget": 0.1,
    "entryTactics": {
        "MARKET_ORDER": {
            "quantityBasisValue": "1000"
        }
    },
    "exitTactics": {
        "LOSS_LIMIT": {
            "amount": "100",
            "lossLimitMode": "AMOUNT"
        },
        "ATR_TRAIL": {
            "pctOfAtr": 1,
            "ATR": {
                "candlesBack": 14,
                "atrType": "WILDER",
                "candleDuration": 1,
                "timeUnit": "MINUTES"
            }
        }
    },
    "setupDetails": {
        "BREAK": {
            "priceToBreak": 1000
        }
    },
    "optionManagement": {
        "optionChoices": {
            "choices": {
                "2022-02-16": [
                    "AAPL20220216C50",
                    "AAPL20220216C51",
                    "AAPL20220216C52",
                ]
            },
            "dataSource": "SIMULATED"
        },
        "expirationDates": [
            "Wed Feb 16 18:33:10 EST 2022",
            "Wed Feb 23 18:33:10 EST 2022"
        ],
        "buyOrSellOption": "LONG",
        "OTM": false,
        "ITM": false,
        "optionChainStrategy": {
            "hugCurrentPrice": {
                "numOfStrikes": 5
            },
        },
        "optionFilters": {
            "tightSpreadPct": {
                "maxOptionSpreadPct": 0.05
            }
        }
    },
    "restartSettings": {
        "maxRestartAttempts": 0,
        "isMaxRestartAttempts": false,
        "maxLoss": 100,
        "isMaxLoss": true,
        "maxMinutesInTradePlan": 0,
        "isMaxMinutesInTradePlan": false,
        "numberOfTrades": 0,
        "isNumberOfTrades": false,
        "deactivateOnRestart": true
    },
    "savedConfigurationTitle": "options",
    "id": 1645054458983
}

const fields = [
  id,
  status,
  brokerage,
  dataSource,
  accountNumber,
  goLong,
  goShort,
  instrument,
  startPeriod,
  strategy (emas RELATED_TRADING_SYSTEM,
    everything else BASIC_TRADING_SYSTEM),
  setup,
  quantityBasis,
  workOrders,
  workOrdersOnEntry,
  workOrdersOnExit,
  terminateWhenFlat,
  restartSystemOncePositionHasFlattened,
  flattenAtMarketClose,
  persistTradePlanOnFailedFiltersOrValidation,
  symbol,
  entry/exit/positionTactics,
  setupDetails,
  setupFilters,
  getAwayOffsetPct,
  pctBetweenBidAsk,
  pctIncreasePerOrderAttempt,
  optionManagement,
  startHour,
  startMin,
  savedConfigurationTitle,
  useSavedConfigTitleForDisplay,
  restartSettings,
  evaluateQuotes,
  useDifferentWorkOrderFieldsOnExit,
  getAwayOffsetPctOnExit,
  pctBetweenBidAskOnExit,
  pctIncreasePerOrderAttemptOnExit,
  useDifferentWorkOrderFieldsOnTarget,
  getAwayOffsetPctOnTarget,
  pctBetweenBidAskOnTarget,
  pctIncreasePerOrderAttemptOnTarget
]

*/
